<template>
    <div class="content">
        <h3 class="mt-2 mb-5 ml-2 is-hidden-desktop"><i class="fal fa-address-book fa-fw mr-3"></i>Trip Contacts</h3>

        <!-- Free-form trip-level admin/office contacts, shown above the compiled
             directory. Staff-typed prose, so any address or URL in it is linked
             on the way out (links Quill already made are left alone). -->
        <div class="white-container content ql-editor" v-if="trip.app_contacts" v-html="introContacts"></div>

        <!-- The trip's contact directory: external parties (restaurants, hotels,
             booking offices, guides, drivers) attached to the trip, its hotels,
             add-ons and itinerary events. Grouped by WHERE the contact hangs off
             the trip -- meal contacts together, hotel contacts together -- which
             is how staff go looking for them. -->
        <div class="mt-5" v-for="group in directoryGroups" :key="group.value">
            <h5 class="ml-2 mb-4"><i class="far fa-fw mr-2" :class="group.icon"></i>{{ group.label }}</h5>
            <div class="white-container">
                <div class="contact-card" v-for="entry in group.contacts" :key="entry.key">
                    <div class="contact-name">
                        {{ entry.primary }}
                        <span class="contact-role" v-if="entry.role"> &middot; {{ entry.role }}</span>
                    </div>
                    <div class="contact-line" v-if="entry.secondary">
                        {{ entry.secondary }}<template v-if="entry.title"> &middot; {{ entry.title }}</template>
                    </div>
                    <div class="contact-line" v-else-if="entry.title">{{ entry.title }}</div>
                    <div class="contact-line" v-for="email in entry.emails" :key="'e' + email">
                        <i class="far fa-envelope fa-fw mr-2"></i><a :href="'mailto:' + email">{{ email }}</a>
                    </div>
                    <div class="contact-line" v-for="phone in entry.phones" :key="'p' + phone">
                        <i class="far fa-phone fa-fw mr-2"></i><a :href="'tel:' + telLink(phone)">{{ telLink(phone) }}</a>
                    </div>
                    <div class="contact-line" v-if="entry.website">
                        <i class="far fa-globe fa-fw mr-2"></i><a :href="webLink(entry.website)" target="_blank" rel="noopener">{{ entry.website }}</a>
                    </div>
                    <div class="contact-line" v-if="entry.location"><i class="far fa-location-dot fa-fw mr-2"></i>{{ entry.location }}</div>
                    <div class="contact-line" v-if="entry.notes">
                        <i class="far fa-sticky-note fa-fw mr-2"></i><span v-html="linkify(entry.notes)"></span>
                    </div>
                    <!-- Where on the trip this contact is used. One line per
                         attachment, so the same driver on five transfers reads
                         as one contact with five uses. -->
                    <div class="contact-source" v-for="use in entry.uses" :key="use.link_id">
                        <i class="far fa-link fa-fw mr-2"></i>{{ use.title }}
                        <span v-if="use.day_on"> &middot; {{ use.day_on | moment('ddd, MMM Do') }}</span>
                        <span v-if="use.note"> &middot; {{ use.note }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Auto-compiled full contact list pulled from every trip record (staff only).
             Unlike the Travelers roster, this ignores the travelers' profile share flags. -->
        <div class="mt-5" v-if="loading">
            <h5 class="has-text-grey"><i class="fal fa-tennis-ball fa-spin mr-3"></i>Gathering contacts from the trip records...</h5>
        </div>
        <div class="mt-5" v-else-if="contacts.length">
            <h5 class="ml-2 mb-4"><i class="far fa-address-book fa-fw mr-2"></i>Participants</h5>
            <div class="white-container">
                <div class="contact-card" v-for="c in contacts" :key="c.id">
                    <div class="contact-name">
                        {{ c.last_name }}, {{ c.first_name }}<template v-if="c.middle_name"> {{ c.middle_name }}</template><template v-if="c.preferred_name"> &ldquo;{{ c.preferred_name }}&rdquo;</template>
                        <span class="contact-role" v-if="c.role"> &middot; {{ c.role }}</span>
                    </div>
                    <div class="contact-line" v-if="c.email"><i class="far fa-envelope fa-fw mr-2"></i><a :href="'mailto:' + c.email">{{ c.email }}</a></div>
                    <div class="contact-line" v-if="c.phone"><i class="far fa-phone fa-fw mr-2"></i><a :href="'tel:' + telLink(c.phone)">{{ telLink(c.phone) }}</a></div>
                    <div class="contact-line" v-if="c.phone_2"><i class="far fa-phone fa-fw mr-2"></i><a :href="'tel:' + telLink(c.phone_2)">{{ telLink(c.phone_2) }}</a></div>
                    <div class="contact-line" v-if="fullAddress(c)"><i class="far fa-location-dot fa-fw mr-2"></i>{{ fullAddress(c) }}</div>
                    <!-- Handles link through to the profile when they look like
                         handles; a free-text answer ("ask me") stays plain text. -->
                    <div class="contact-line" v-for="s in socials(c)" :key="s.network">
                        <i class="fab fa-fw mr-2" :class="s.icon"></i>
                        <a v-if="s.href" :href="s.href" target="_blank" rel="noopener">{{ s.handle }}</a>
                        <template v-else>{{ s.handle }}</template>
                    </div>
                </div>
            </div>
        </div>

        <!-- Nothing at all yet -->
        <div class="white-container content has-text-centered"
             v-if="!loading && !directoryLoading && !contacts.length && !directoryGroups.length && !trip.app_contacts">
            <h4 class="pt-6 pb-6 has-text-grey"><i class="fal fa-message-captions mr-2"></i> More information will be available later.  Check back soon!</h4>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import {client as http} from "../../http_client";
    import {linkifyHtml, linkifyText, socialHref, telHref, webHref} from "../../components/links";

    /*
        The sections of the directory, in render order.

        Grouped by what the contact is ATTACHED TO rather than by what the
        contact IS: staff arrive here having just been asked "who do we call
        about dinner", so the restaurant, its booking office and the guide
        meeting the group there belong in one block. The contact's own type is
        still on every card, as the role beside the name.

        `entities` matches entity_contacts.entity_type; `categories` narrows
        trip-events by the staff-picked event type the endpoint ships as
        source.category. Adding a section is one line here.
    */
    const GROUPS = [
        {value: 'trip', label: 'Trip Contacts', icon: 'fa-suitcase-rolling', entities: ['trip']},
        {value: 'hotel', label: 'Hotel Contacts', icon: 'fa-hotel', entities: ['trip-hotels'], categories: ['hotel']},
        {value: 'meal', label: 'Meal Contacts', icon: 'fa-utensils', categories: ['meal']},
        {value: 'transport', label: 'Transportation Contacts', icon: 'fa-bus', categories: ['bus', 'car', 'transfer']},
        {value: 'event', label: 'Event Contacts', icon: 'fa-calendar-star', entities: ['trip-events']},
        {value: 'addon', label: 'Add-On Contacts', icon: 'fa-plus-circle', entities: ['trip-addons']},
        {value: 'other', label: 'Other Contacts', icon: 'fa-address-card'}
    ];

    // Mirrors server/src/components/contact_types.js (and Roger's
    // client/src/components/contact_meta.js). Labels the role shown beside the
    // name, now that type no longer picks the section. Keep the three in sync.
    const TYPE_LABELS = {
        'restaurant': 'Restaurant',
        'hotel': 'Hotel',
        'booking-office': 'Booking Office',
        'vendor': 'Vendor',
        'guide': 'Guide',
        'driver': 'Driver',
        'tennis': 'Tennis / Tournament',
        'emergency': 'Emergency',
        'other': null                       // says nothing -- don't spend a line on it
    };

    const SOCIAL_FIELDS = [
        {network: 'instagram', icon: 'fa-instagram'},
        {network: 'facebook', icon: 'fa-facebook-f'},
        {network: 'twitter', icon: 'fa-twitter'},
        {network: 'whatsapp', icon: 'fa-whatsapp'}
    ];

    /*
        Which section a link belongs in. Event links are filed by the event's
        own category, so the restaurant on a dinner lands under Meals and the
        driver on the airport transfer under Transportation; an event type with
        no section of its own falls through to Event Contacts.

        A source the client doesn't recognise (a new entity_type from a newer
        server) lands in Other rather than vanishing.
    */
    function groupFor(source) {
        if (!source) return 'other';

        const byCategory = source.category
            ? GROUPS.find(g => (g.categories || []).indexOf(source.category) > -1)
            : null;
        if (byCategory) return byCategory.value;

        const byEntity = GROUPS.find(g => (g.entities || []).indexOf(source.entity_type) > -1);
        return byEntity ? byEntity.value : 'other';
    }

    export default {
        data: function() {
            return {
                loading: false,
                directoryLoading: false,
                contacts: [],
                directory: []
            };
        },
        computed: {
            ...mapGetters(['trip', 'registration']),
            isStaff: function() {
                return this.registration.type === 'staff' || this.registration.second_type === 'staff';
            },
            introContacts: function() {
                return linkifyHtml(this.trip.app_contacts);
            },
            /*
                The endpoint returns one row per ATTACHMENT so provenance
                survives; collapse to one entry per contact WITHIN a section,
                carrying the uses as sub-lines. A contact used in two sections
                (the hotel that also hosts a dinner) appears in both, each time
                listing only the uses that belong there -- which is the point of
                sectioning by attachment.
            */
            directoryGroups: function() {
                const byGroup = {};

                this.directory.forEach(row => {
                    const group = groupFor(row.source);
                    if (!byGroup[group]) byGroup[group] = {};

                    let entry = byGroup[group][row.id];
                    if (!entry) {
                        entry = byGroup[group][row.id] = {
                            id: row.id,
                            key: group + ':' + row.id,
                            // Same display rule as Roger and the server.
                            primary: row.company || row.name || '',
                            secondary: row.company ? row.name : null,
                            role: TYPE_LABELS[row.role || row.type] || null,
                            title: row.title,
                            emails: [row.email, row.email_2].filter(Boolean),
                            phones: [row.phone, row.phone_2].filter(Boolean),
                            website: row.website,
                            location: [row.address, row.city, row.country].filter(Boolean).join(', '),
                            notes: row.directory_notes,
                            sortDay: null,
                            uses: []
                        };
                    }

                    if (row.source && row.source.title) {
                        entry.uses.push({
                            link_id: row.link_id,
                            title: row.source.title,
                            day_on: row.source.day_on,
                            note: row.label || row.link_notes || null
                        });

                        // Earliest use orders the section, so the dated ones
                        // (meals, transport, events) read in trip order.
                        if (row.source.day_on && (!entry.sortDay || row.source.day_on < entry.sortDay)) {
                            entry.sortDay = row.source.day_on;
                        }
                    }
                });

                return GROUPS
                    .map(g => ({
                        value: g.value,
                        label: g.label,
                        icon: g.icon,
                        contacts: Object.keys(byGroup[g.value] || {})
                            .map(id => byGroup[g.value][id])
                            // The uses under one card read in trip order too --
                            // the endpoint orders by ec.sort, which says nothing
                            // about when in the trip each one falls.
                            .map(entry => {
                                entry.uses.sort((a, b) => {
                                    const da = a.day_on || '';
                                    const db = b.day_on || '';
                                    return da < db ? -1 : (da > db ? 1 : 0);
                                });
                                return entry;
                            })
                            .sort((a, b) => {
                                const da = a.sortDay || '';
                                const db = b.sortDay || '';
                                if (da !== db) return da < db ? -1 : 1;
                                const na = (a.primary || '').toLowerCase();
                                const nb = (b.primary || '').toLowerCase();
                                return na < nb ? -1 : (na > nb ? 1 : 0);
                            })
                    }))
                    .filter(g => g.contacts.length);
            }
        },
        methods: {
            telLink: telHref,
            webLink: webHref,
            linkify: linkifyText,
            socials: function(c) {
                return SOCIAL_FIELDS
                    .filter(f => c[f.network])
                    .map(f => ({
                        network: f.network,
                        icon: f.icon,
                        handle: c[f.network],
                        href: socialHref(f.network, c[f.network])
                    }));
            },
            fullAddress: function(c) {
                const cityLine = [c.city, c.state].filter(Boolean).join(', ');
                return [c.address, c.address_2, [cityLine, c.zip].filter(Boolean).join(' '), c.country]
                    .filter(Boolean).join(', ').trim();
            },
            loadContacts: function() {
                if (!this.registration.id || !this.isStaff) return;
                this.loading = true;
                http.get('/app/trips/' + this.registration.trip_id + '/staff/contacts').then(response => {
                    this.contacts = response.data || [];
                    this.loading = false;
                }).catch(() => {
                    this.loading = false;
                });
            },
            loadDirectory: function() {
                if (!this.registration.id || !this.isStaff) return;
                this.directoryLoading = true;
                http.get('/app/trips/' + this.registration.trip_id + '/staff/directory').then(response => {
                    this.directory = response.data || [];
                    this.directoryLoading = false;
                }).catch(() => {
                    this.directoryLoading = false;
                });
            }
        },
        watch: {
            registration: function() {
                this.loadContacts();
                this.loadDirectory();
            }
        },
        mounted() {
            this.loadContacts();
            this.loadDirectory();
        }
    };
</script>

<style lang="scss" scoped>
    .contact-card {
        border-left: 3px solid #628b21;
        padding-left: 0.7rem;
        padding-bottom: 0.9rem;
        margin-bottom: 0.9rem;
        border-bottom: 1px solid #ededed;

        &:last-child {
            padding-bottom: 0;
            margin-bottom: 0;
            border-bottom: 0;
        }

        .contact-name {
            font-weight: 700;
            margin-bottom: 0.3rem;
        }

        .contact-role {
            font-weight: 400;
            color: #6b6b6b;
            font-size: 0.85rem;
            text-transform: capitalize;
        }

        .contact-line {
            font-size: 0.95rem;
            margin-bottom: 0.15rem;
        }

        // Where a directory contact is used. Quieter than the contact's own
        // details -- it's context, not the number you're after.
        .contact-source {
            font-size: 0.85rem;
            color: #6b6b6b;
            margin-top: 0.25rem;
        }
    }
</style>
